import React from 'react';
import { Link } from 'react-router-dom';
import BrandLogo from '../components/BrandLogo';

const Section = ({ num, title, children }) => (
  <section className="mt-11">
    <h2 className="flex items-baseline gap-2.5 border-b border-border pb-2 font-outfit text-xl">
      <span className="tabular-nums text-secondary">{num}</span>
      {title}
    </h2>
    <div className="mt-3 space-y-3 leading-relaxed text-foreground/90 [&_li]:mb-1.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5">
      {children}
    </div>
  </section>
);

const Code = ({ children }) => (
  <code className="rounded bg-muted px-1.5 py-0.5 text-[0.85em] text-foreground">{children}</code>
);

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b-4 border-[#34D0E3] bg-gradient-to-br from-[#0409A3] to-[#141a5c] px-6 py-14 text-white">
        <div className="mx-auto max-w-3xl">
          <Link to="/" className="mb-6 inline-block">
            <BrandLogo variant="white" className="h-6" />
          </Link>
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#34D0E3]">
            Selltrix · Extensión para Chrome
          </p>
          <h1 className="font-outfit text-3xl uppercase leading-tight sm:text-4xl">
            Política de Privacidad
          </h1>
          <p className="mt-4 text-sm text-[#C9D2FF]">
            Última actualización: 3 de septiembre de 2026 · Vigente desde: 28 de julio de 2026
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-11">
        <p className="text-lg leading-relaxed">
          Esta política explica qué información procesa la extensión de Google Chrome de Selltrix
          (la “Extensión”), con quién se comunica esa información y qué derechos tienes sobre ella.
          Aplica únicamente a la Extensión; el uso del sitio web y de la plataforma Selltrix se rige
          por la política de privacidad publicada en{' '}
          <a href="https://selltrix.ai.com" className="text-primary hover:underline">selltrix.ai.com</a>.
        </p>

        <div className="my-6 rounded-lg border border-border border-l-4 border-l-primary bg-card p-5">
          <p>
            <strong>En una frase:</strong> la Extensión lee páginas públicas de Mercado Libre dentro de
            tu navegador y usa tu cuenta de Selltrix para consultar, en el backend de Selltrix y en la
            API oficial de Mercado Libre, el número de visitas de cada publicación. Nunca accedemos a tu
            contraseña de Mercado Libre, no vendemos tu información, no la usamos para publicidad y no
            la compartimos con terceros ajenos a Mercado Libre.
          </p>
        </div>

        <Section num="01" title="Responsable">
          <p>
            <strong>Mercado PRO LLC</strong>, sociedad constituida en los Estados Unidos, es la
            responsable de la Extensión.
          </p>
          <p>Correo de contacto para asuntos de privacidad: <strong>privacidad@selltrix.com</strong></p>
        </Section>

        <Section num="02" title="Cómo funciona la Extensión (visión general)">
          <p>La Extensión combina tres tipos de procesamiento, detallados en las secciones 03 a 06:</p>
          <ul>
            <li>
              <strong>Lectura local de páginas de Mercado Libre:</strong> extrae en tu navegador los
              datos visibles de las publicaciones (título, precio, envío, etc.) para calcular métricas.
              Esto ocurre por completo en tu equipo.
            </li>
            <li>
              <strong>Autenticación con tu cuenta de Selltrix:</strong> para saber quién eres y a qué
              cuenta vendedora de Mercado Libre estás conectado, la Extensión inicia sesión contra el
              backend de Selltrix con el correo y la contraseña que ingresas en su ícono.
            </li>
            <li>
              <strong>Consulta a la API oficial de Mercado Libre:</strong> con un token de acceso de
              corta duración que obtiene de nuestro backend, la Extensión llama directamente al endpoint
              público de visitas de Mercado Libre (<Code>api.mercadolibre.com</Code>) por cada
              publicación que estás analizando.
            </li>
          </ul>
        </Section>

        <Section num="03" title="Tu cuenta de Selltrix: inicio de sesión y token de sesión">
          <p>
            Cuando inicias sesión desde el ícono de la Extensión —o cuando la app web de Selltrix la
            activa automáticamente porque ya iniciaste sesión ahí— tu correo y tu contraseña se envían,
            cifrados por HTTPS, al backend de Selltrix (<Code>POST /api/auth/login</Code>) únicamente
            para verificarlos.
          </p>
          <ul>
            <li>La Extensión <strong>nunca almacena tu contraseña</strong>: solo la envía en ese momento para autenticarte.</li>
            <li>
              Si la autenticación es correcta, el backend responde con un token de sesión (JWT) que la
              Extensión guarda en <Code>chrome.storage.local</Code>, en tu equipo, y reenvía en cada
              solicitud posterior para identificarte —el equivalente a mantener la sesión iniciada.
            </li>
            <li>
              Ese token no es tu contraseña de Mercado Libre ni de Selltrix: es una credencial de sesión
              de vida corta que puedes revocar cerrando sesión en la Extensión o en la plataforma.
            </li>
            <li>Si el token expira, la Extensión lo borra automáticamente y te pide iniciar sesión de nuevo.</li>
          </ul>
        </Section>

        <Section num="04" title="Consulta de visitas a la API de Mercado Libre">
          <p>
            Para calcular las visitas y la facturación estimada de una publicación, la Extensión
            necesita el dato oficial de visitas de Mercado Libre, que solo Mercado Libre puede entregar:
          </p>
          <ol>
            <li>
              La Extensión pide a nuestro backend (<Code>GET /api/research/token</Code>), usando tu
              token de sesión de Selltrix, un token de acceso de la API de Mercado Libre asociado a la
              cuenta vendedora que ya conectaste en la plataforma Selltrix.
            </li>
            <li>
              Con ese token, la Extensión llama directamente —desde tu navegador— al endpoint público{' '}
              <Code>api.mercadolibre.com/items/&#123;itemId&#125;/visits/time_window</Code> por cada
              publicación que estás analizando.
            </li>
            <li>
              El identificador de la publicación (<Code>itemId</Code>, un dato público y no personal) es
              lo único que viaja en esa consulta; la respuesta (número de visitas) se usa para calcular
              la facturación estimada en tu propio navegador.
            </li>
          </ol>
          <p>
            Esa llamada la recibe Mercado Libre, no un tercero ajeno: es la misma API que usa la
            plataforma web de Selltrix y que tú mismo autorizaste al conectar tu cuenta vendedora
            mediante el inicio de sesión oficial de Mercado Libre.
          </p>
        </Section>

        <Section num="05" title="Qué otra información procesa la Extensión (local)">
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-[#0409A3] text-left text-white">
                  <th className="px-3.5 py-3 text-xs font-semibold uppercase tracking-wider">Categoría</th>
                  <th className="px-3.5 py-3 text-xs font-semibold uppercase tracking-wider">Qué incluye</th>
                  <th className="px-3.5 py-3 text-xs font-semibold uppercase tracking-wider">Dónde queda</th>
                </tr>
              </thead>
              <tbody className="bg-card">
                <tr className="border-t border-border">
                  <td className="px-3.5 py-3 align-top font-medium">Contenido de páginas de Mercado Libre</td>
                  <td className="px-3.5 py-3 align-top">
                    Datos visibles de las publicaciones que abres: título, precio, cantidad vendida,
                    categoría, reputación del vendedor, condiciones de envío.
                  </td>
                  <td className="px-3.5 py-3 align-top">
                    En la memoria del navegador durante la sesión. Se descarta al cerrar la pestaña.
                  </td>
                </tr>
                <tr className="border-t border-border">
                  <td className="px-3.5 py-3 align-top font-medium">Credenciales e identidad de Selltrix</td>
                  <td className="px-3.5 py-3 align-top">
                    Correo y contraseña (solo al iniciar sesión) y el token de sesión generado después.
                    Ver sección 03.
                  </td>
                  <td className="px-3.5 py-3 align-top">
                    El correo/contraseña se envían una vez al backend de Selltrix. El token queda en{' '}
                    <Code>chrome.storage.local</Code>, en tu equipo.
                  </td>
                </tr>
                <tr className="border-t border-border">
                  <td className="px-3.5 py-3 align-top font-medium">Identificador de publicación (itemId)</td>
                  <td className="px-3.5 py-3 align-top">El ID público de cada publicación que analizas.</td>
                  <td className="px-3.5 py-3 align-top">
                    Se envía a la API de Mercado Libre para consultar sus visitas. Ver sección 04.
                  </td>
                </tr>
                <tr className="border-t border-border">
                  <td className="px-3.5 py-3 align-top font-medium">Preferencias y configuración</td>
                  <td className="px-3.5 py-3 align-top">
                    Moneda, márgenes y costos que tú ingresas para el cálculo de rentabilidad, filtros
                    guardados, funciones activadas.
                  </td>
                  <td className="px-3.5 py-3 align-top">
                    Almacenamiento local del navegador (<Code>chrome.storage.local</Code>), en tu equipo.
                  </td>
                </tr>
                <tr className="border-t border-border">
                  <td className="px-3.5 py-3 align-top font-medium">Resultados y cálculos</td>
                  <td className="px-3.5 py-3 align-top">
                    Métricas y análisis que genera la Extensión a partir de lo anterior.
                  </td>
                  <td className="px-3.5 py-3 align-top">
                    Se generan y se muestran localmente. Solo salen de tu equipo si tú los exportas,
                    copias manualmente o los ves a través de la app web de Selltrix (sección 06).
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="mt-6 mb-1.5 text-base font-semibold">Qué NO hace la Extensión</h3>
          <ul>
            <li>No recopila tu nombre, teléfono ni datos de identificación más allá del correo que usas para iniciar sesión en Selltrix.</li>
            <li>No accede ni conoce tu contraseña de Mercado Libre: la conexión con tu cuenta vendedora se hace mediante el inicio de sesión oficial de Mercado Libre en la plataforma web, nunca a través de la Extensión.</li>
            <li>No procesa datos de tarjetas ni medios de pago.</li>
            <li>
              No lee tu historial de navegación ni el contenido de otras pestañas: solo se ejecuta en
              dominios de Mercado Libre y en los dominios propios de Selltrix.
            </li>
            <li>No incluye rastreadores, píxeles publicitarios ni herramientas de analítica de terceros.</li>
            <li>No recopila datos sensibles: salud, ubicación precisa, creencias, orientación u origen.</li>
          </ul>
        </Section>

        <Section num="06" title="Modo dirigido: cuando la app web de Selltrix activa una búsqueda">
          <p>
            Si usas la plataforma web de Selltrix y ella activa el “modo dirigido” de investigación de
            mercado, ocurre lo siguiente:
          </p>
          <ul>
            <li>La app web le envía a la Extensión, mediante un mensaje dentro de tu propio navegador (no por internet), hasta 4 términos de búsqueda y tu token de sesión.</li>
            <li>La Extensión abre esas búsquedas en pestañas de Mercado Libre, lee localmente los resultados visibles y consulta sus visitas como se describe en la sección 04.</li>
            <li>
              El resultado (publicaciones, precios y visitas —datos públicos de Mercado Libre, no
              datos tuyos) se devuelve a la pestaña de la app web de Selltrix para que lo muestre. Desde
              ahí, la plataforma puede guardarlo en tu cuenta conforme a la política de privacidad de{' '}
              <a href="https://selltrix.ai.com" className="text-primary hover:underline">selltrix.ai.com</a>.
            </li>
          </ul>
        </Section>

        <Section num="07" title="Permisos que solicita y por qué">
          <ul>
            <li>
              <Code>storage</Code> — guardar en tu equipo tu token de sesión y tus preferencias, para no
              pedirte iniciar sesión ni volver a configurar la Extensión en cada visita.
            </li>
            <li>
              <Code>tabs</Code> — abrir y cerrar las pestañas de Mercado Libre que la Extensión necesita
              leer en el modo dirigido, y detectar si la pestaña activa es de Mercado Libre en el modo
              autónomo.
            </li>
            <li>
              <Code>notifications</Code> — avisarte con una notificación del sistema cuando tu sesión de
              Selltrix expira y necesitas iniciar sesión de nuevo.
            </li>
            <li>
              Acceso a los dominios de Mercado Libre por país (<Code>*.mercadolibre.com.co</Code>,{' '}
              <Code>*.mercadolibre.com.ar</Code>, <Code>*.mercadolibre.com.mx</Code>,{' '}
              <Code>*.mercadolibre.cl</Code>, <Code>*.mercadolibre.com.pe</Code>,{' '}
              <Code>*.mercadolivre.com.br</Code>) y a <Code>api.mercadolibre.com</Code> — para leer las
              publicaciones y consultar la API oficial de visitas.
            </li>
            <li>
              Acceso a los dominios propios de Selltrix (<Code>selltrix.ai</Code>, <Code>selltrix.app</Code>{' '}
              y los entornos de despliegue de la plataforma) — para autenticarte y para intercambiar
              mensajes con la app web en el modo dirigido.
            </li>
          </ul>
        </Section>

        <Section num="08" title="Uso limitado (Chrome Web Store)">
          <p>
            El uso que Mercado PRO LLC hace de la información descrita arriba cumple la{' '}
            <a
              href="https://developer.chrome.com/docs/webstore/program-policies/limited-use"
              className="text-primary hover:underline"
            >
              Política de Uso Limitado de Chrome Web Store
            </a>. La usamos únicamente para operar las funciones visibles para ti —autenticación,
            cálculo de visitas y facturación estimada—, no la vendemos, no la transferimos a terceros
            ajenos a Mercado Libre para fines distintos de prestar el servicio, no la usamos ni la
            transferimos con fines publicitarios ni de segmentación, y no la utilizamos para determinar
            solvencia ni para otorgar préstamos. Ningún humano la revisa manualmente, salvo para
            responder a una solicitud de soporte tuya o por obligación legal.
          </p>
        </Section>

        <Section num="09" title="Conservación y eliminación">
          <ul>
            <li>Los datos de las publicaciones que lees (título, precio, visitas, etc.) se descartan al cerrar la pestaña o el navegador; la Extensión no los guarda.</li>
            <li>Tu token de sesión y tus preferencias permanecen en <Code>chrome.storage.local</Code> hasta que cierres sesión, el token expire o desinstales la Extensión.</li>
            <li>
              Para eliminar todo: usa “Cerrar sesión” en el ícono de la Extensión, o entra a{' '}
              <Code>chrome://extensions</Code>, ubica Selltrix y selecciona “Quitar de Chrome” — eso
              borra por completo el almacenamiento local de la Extensión.
            </li>
            <li>
              Tu cuenta de Selltrix y los datos que guarda esa plataforma (no la Extensión) se rigen por
              la política de privacidad de{' '}
              <a href="https://selltrix.ai.com" className="text-primary hover:underline">selltrix.ai.com</a>.
            </li>
          </ul>
        </Section>

        <Section num="10" title="Terceros">
          <p>La Extensión comparte información con dos destinos, ambos descritos arriba, y con ningún otro:</p>
          <ul>
            <li>
              <strong>Mercado Libre</strong> (<Code>api.mercadolibre.com</Code>): recibe el identificador
              público de cada publicación que analizas, para devolver su número de visitas. Se rige por
              la política de privacidad de Mercado Libre.
            </li>
            <li>
              <strong>Backend de Selltrix</strong> (infraestructura propia de Mercado PRO LLC, no un
              tercero externo): recibe tu correo y contraseña al iniciar sesión, y tu token de sesión en
              cada solicitud posterior, para autenticarte y entregar el token de Mercado Libre necesario
              para el punto anterior.
            </li>
          </ul>
          <p>
            No compartimos información con anunciantes, corredores de datos ni ningún otro tercero. Esta
            página web está alojada en <strong>GoDaddy</strong>, proveedor que puede registrar datos
            técnicos estándar de visita a este sitio (dirección IP, navegador, fecha) conforme a sus
            propias políticas; ese registro corresponde a la visita a esta página, no al uso de la
            Extensión.
          </p>
        </Section>

        <Section num="11" title="Seguridad">
          <p>
            Toda comunicación de la Extensión con el backend de Selltrix y con la API de Mercado Libre
            viaja cifrada por HTTPS/TLS. Tu contraseña de Selltrix se transmite solo en el momento de
            iniciar sesión y nunca queda guardada por la Extensión; el token de sesión que la reemplaza
            se guarda únicamente en tu equipo. La Extensión se distribuye solo a través de Chrome Web
            Store, con código firmado y revisado por Google, y se actualiza por ese mismo canal. La
            protección de lo que se guarda localmente depende también de la seguridad de tu equipo y de
            tu perfil de Chrome.
          </p>
        </Section>

        <Section num="12" title="Tus derechos">
          <p>
            Puedes consultar, revocar o eliminar en cualquier momento lo que la Extensión guarda en tu
            navegador (sección 09). Tu correo y tu contraseña solo pasan por nuestros servidores en el
            instante de autenticarte y no quedan almacenados por la Extensión; el manejo de tu cuenta y
            de los datos asociados a ella en la plataforma Selltrix se describe en la política de
            privacidad de{' '}
            <a href="https://selltrix.ai.com" className="text-primary hover:underline">selltrix.ai.com</a>.
          </p>
          <p>
            Si deseas ejercer derechos de acceso, corrección, supresión, portabilidad, oposición o
            revocación de autorización sobre los datos que procesamos, escríbenos a{' '}
            <strong>privacidad@selltrix.com</strong> y responderemos dentro de los plazos legales. Estos
            derechos están reconocidos por la Ley 1581 de 2012 en Colombia —con posibilidad de reclamar
            ante la Superintendencia de Industria y Comercio—, por el RGPD en la Unión Europea y el
            Reino Unido, y por la CCPA/CPRA en California. No vendemos ni compartimos información
            personal según la definición de ninguna de esas normas.
          </p>
        </Section>

        <Section num="13" title="Menores de edad">
          <p>
            La Extensión está dirigida a personas mayores de 18 años que venden en Mercado Libre y no
            está diseñada para menores.
          </p>
        </Section>

        <Section num="14" title="Relación con Mercado Libre">
          <p>
            Selltrix es un producto independiente de Mercado PRO LLC. No está afiliado, patrocinado ni
            respaldado por MercadoLibre, Inc. ni sus filiales. “Mercado Libre” es una marca de su
            respectivo titular y se menciona con fines exclusivamente descriptivos.
          </p>
        </Section>

        <Section num="15" title="Cambios en esta política">
          <p>
            Si modificamos esta política actualizaremos la fecha del encabezado. Cuando el cambio sea
            sustancial —por ejemplo, si agregamos una nueva integración o un nuevo destinatario de
            datos— lo anunciaremos dentro de la Extensión antes de que entre en vigor.
          </p>
        </Section>

        <Section num="16" title="Contacto">
          <p>
            <strong>Mercado PRO LLC</strong><br />
            Correo: <strong>privacidad@selltrix.com</strong><br />
            Sitio: <a href="https://selltrix.ai.com" className="text-primary hover:underline">selltrix.ai.com</a>
          </p>
        </Section>

        <p className="mt-14 border-t border-border pt-5 text-sm text-muted-foreground">
          © 2026 Mercado PRO LLC. Documento informativo; no constituye asesoría legal.
        </p>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
